//
// # Numeric IDs & ID Maps
// Widely used as "pointers" throughout our schema,
// e.g. when updating child elements.
//

import type { LamportTimestamp } from "CoreUtils";
import { IEntityMap, HasId } from "./entity_map";

// Re-export HasId for backward compatibility
export type { HasId } from "./entity_map";

// Max value for i32, matching the Rust-side Id type (i32)
const INT32_MAX = 2 ** 31 - 1;

// Generate a random ID in [1, INT32_MAX] to avoid collisions
// between independently-running client and server instances.
export function randomId(): number {
  return Math.floor(Math.random() * INT32_MAX) + 1;
}

// # ID Map
// Keep track of entities by their ID, and give each a unique ID when added.
// API is modeled after `Set`, which this frequently replaces.
//
// This is the non-collaborative implementation with zero CRDT overhead.
// For collaborative editing, use CRDTMap instead.
// Both implement IEntityMap for interchangeability.
export class IdMap<Value extends HasId> implements IEntityMap<Value> {
  private readonly _map = new Map<number, Value>();

  // Note: timestamp parameter is accepted for IEntityMap compatibility but ignored.
  // IdMap doesn't need timestamps - only CRDTMap uses them for conflict resolution.
  add = (val: Value, _timestamp?: LamportTimestamp): void => {
    // Preserve existing IDs (e.g. from GUI) instead of overwriting.
    // Only assign a new random ID if one is not already set.
    const id = val.id ?? randomId();
    val.id = id;
    this._map.set(id, val);
  };

  get = (id: number): Value | undefined => this._map.get(id);

  remove = (id: number, _timestamp?: LamportTimestamp): boolean =>
    this._map.delete(id);

  forEach = (fn: (val: Value) => void): void => this._map.forEach(fn);

  values = (): IterableIterator<Value> => this._map.values();

  size = (): number => this._map.size;

  update = (
    val: Partial<Value> & { id: number },
    _timestamp?: LamportTimestamp
  ): boolean => {
    const { id } = val;
    const existing = this.get(id);
    if (!existing) return false;
    this._map.set(id, { ...existing, ...val });
    return true;
  };
}
